<template>
  <UiModalShell :show="props.showModal" title="Edit User" @close="closeModal">
    <!-- Loading Overlay -->
    <div
      v-if="loading"
      class="absolute inset-0 bg-bg-1/75 flex items-center justify-center z-10 rounded-[var(--radius-lg)]"
    >
      <div class="flex flex-col items-center">
        <Loader2 :size="32" class="animate-spin text-lime" />
        <p class="mt-2 text-sm text-text-2">Saving changes...</p>
      </div>
    </div>

    <form id="user-edit-form" @submit.prevent="submitForm" class="space-y-4">
      <!-- Error Message -->
      <div
        v-if="error"
        class="p-3 bg-[rgba(240,82,82,0.08)] border border-err/40 rounded-[var(--radius-md)]"
      >
        <p class="text-err text-sm">{{ error }}</p>
      </div>

      <!-- Success Message -->
      <div
        v-if="success"
        class="p-3 bg-[rgba(132,204,22,0.08)] border border-lime/40 rounded-[var(--radius-md)]"
      >
        <p class="text-lime text-sm">{{ success }}</p>
      </div>

      <!-- First Name -->
      <div>
        <label class="block text-xs font-medium text-text-2 mb-1">
          First Name <span class="text-err">*</span>
        </label>
        <UiInput
          v-model="form.first_name"
          :left-icon="User"
          placeholder="Enter first name"
        />
      </div>

      <!-- Last Name -->
      <div>
        <label class="block text-xs font-medium text-text-2 mb-1">
          Last Name <span class="text-err">*</span>
        </label>
        <UiInput
          v-model="form.last_name"
          :left-icon="User"
          placeholder="Enter last name"
        />
      </div>

      <!-- Email (Read-only) -->
      <div>
        <label class="block text-xs font-medium text-text-2 mb-1">Email</label>
        <UiInput
          :model-value="props.user?.email"
          type="email"
          :left-icon="Mail"
          disabled
        />
        <p class="text-[11px] text-text-3 mt-1">Email cannot be changed</p>
      </div>

      <!-- Contact Number -->
      <div>
        <label class="block text-xs font-medium text-text-2 mb-1">
          Contact Number <span class="text-err">*</span>
        </label>
        <UiInput
          v-model="form.contact_number"
          type="tel"
          :left-icon="Phone"
          placeholder="Enter contact number"
        />
      </div>

      <!-- Account Type (Read-only) -->
      <div>
        <label class="block text-xs font-medium text-text-2 mb-1">Account Type</label>
        <div class="flex items-center gap-2">
          <span
            class="px-3 py-1 text-xs font-semibold rounded-full"
            :class="props.user?.account_type === 'CUSTOMER' ? 'bg-lime/15 text-lime' : 'bg-[rgba(168,85,247,0.15)] text-[rgb(192,132,252)]'"
          >
            {{ props.user?.account_type || 'N/A' }}
          </span>
          <span v-if="props.user?.role !== 'PORTAL_SUPER_ADMIN'" class="text-[11px] text-text-3">
            (Use the Assign button to manage {{ props.user?.account_type === 'CUSTOMER' ? 'customers' : 'groups' }})
          </span>
        </div>
      </div>

      <!-- Role (Read-only) -->
      <div>
        <label class="block text-xs font-medium text-text-2 mb-1">Role</label>
        <span class="px-3 py-1 text-xs font-semibold rounded-full bg-[rgba(59,130,246,0.15)] text-[rgb(96,165,250)]">
          {{ roleLabel(props.user?.role) }}
        </span>
      </div>
    </form>

    <template #footer>
      <UiButton variant="ghost" :disabled="loading" @click="closeModal">
        Cancel
      </UiButton>
      <UiButton
        variant="primary"
        type="submit"
        form="user-edit-form"
        :loading="loading"
        @click="submitForm"
      >
        Save Changes
      </UiButton>
    </template>
  </UiModalShell>
</template>

<script setup>
import { ref, watch, reactive } from 'vue';
import { User, Mail, Phone, Loader2 } from 'lucide-vue-next';
import UiModalShell from '@/components/ui/UiModalShell.vue';
import UiInput from '@/components/ui/UiInput.vue';
import UiButton from '@/components/ui/UiButton.vue';

const props = defineProps({
  showModal: {
    type: Boolean,
    default: false
  },
  user: {
    type: Object,
    default: null
  }
});

const emit = defineEmits(['close', 'save']);

const loading = ref(false);
const error = ref(null);
const success = ref(null);

const form = reactive({
  first_name: '',
  last_name: '',
  contact_number: ''
});

// Watch for user changes and populate form
watch(
  () => props.user,
  (newUser) => {
    if (newUser) {
      form.first_name = newUser.first_name || '';
      form.last_name = newUser.last_name || '';
      form.contact_number = newUser.contact_number || '';
    }
  },
  { immediate: true }
);

// Reset messages when modal opens
watch(
  () => props.showModal,
  (isOpen) => {
    if (isOpen) {
      error.value = null;
      success.value = null;
    }
  }
);

// Friendly display label for a role value; PORTAL_SUPER_ADMIN gets a
// human-readable label, all other roles keep the raw uppercase form.
const roleLabel = (role) => {
  if (!role) return 'N/A';
  if (role === 'PORTAL_SUPER_ADMIN') return 'Portal Super Admin';
  return role.replaceAll('_', ' ');
};

const closeModal = () => {
  if (loading.value) return;
  error.value = null;
  success.value = null;
  emit('close');
};

const submitForm = () => {
  error.value = null;
  success.value = null;

  // Validate form
  if (!form.first_name.trim()) {
    error.value = 'First name is required';
    return;
  }
  if (!form.last_name.trim()) {
    error.value = 'Last name is required';
    return;
  }
  if (!form.contact_number.trim()) {
    error.value = 'Contact number is required';
    return;
  }

  // Emit save event with form data
  emit('save', {
    user_id: props.user.id,
    first_name: form.first_name.trim(),
    last_name: form.last_name.trim(),
    contact_number: form.contact_number.trim()
  });
};

// Expose loading setter for parent component
const setLoading = (value) => {
  loading.value = value;
};

const setError = (message) => {
  error.value = message;
};

const setSuccess = (message) => {
  success.value = message;
};

defineExpose({ setLoading, setError, setSuccess });
</script>

<style scoped>
/* Tailwind handles all styling */
</style>
